import { ColumnMappingService, ColumnMappingHelper, ColumnExportData } from '@/core/services/column-mapping.service';
import { ReportSettings } from '@/core/models/report-settings.interface';
import { Layer } from '@/core/models/layer';
import { ActivityDetailVm } from '@/core/models/activity';
import { mkConfig, generateCsv, download } from 'export-to-csv';
import moment from 'moment';

// from export-to-csv/output/lib/types.d.ts (sadly not exported by export-to-csv module)
type AcceptedData = number | string | boolean | null | undefined;

export class CsvExporter {
    constructor(
        private readonly columnMappingService: ColumnMappingService,
        private readonly reportSettings: ReportSettings,
        private readonly layers: Layer[],
    ) {}

    private getExportData(activities: ActivityDetailVm[]): Record<string, AcceptedData>[] {
        const columnData: ColumnExportData[] = ColumnMappingHelper.getColumnExportData(
            this.columnMappingService,
            this.reportSettings.tableColumns,
            this.layers,
        );

        return activities.map((activity: ActivityDetailVm) => {
            const transformedActivity: Record<string, AcceptedData> = {};

            // Iterate through each column, checking visibility and applying transformations
            columnData.forEach((column: ColumnExportData) => {
                if (column.columnVisible) {
                    switch (column.columnKey) {
                        case 'activities_time':
                            const [startTime, endTime] = activity.time.split(' - ');
                            transformedActivity['Start Time'] = startTime.trim();
                            transformedActivity['End Time'] = endTime.trim().replace(/\(\+\d+\)/, '');
                            break;
                        case 'activities_date':
                            const dateFormat = this.reportSettings.dateFormat;
                            transformedActivity['Start Date'] = moment(activity.start).format(dateFormat);
                            transformedActivity['End Date'] = moment(activity.end).format(dateFormat);
                            break;
                        default:
                            const columnName = column.columnName;
                            const activityKey = column.columnActivityKey;
                            if (activityKey === undefined) {
                                console.debug('CSV export: Unknown column key for activity mapping:', column.columnKey);
                            } else {
                                transformedActivity[columnName] = activity[activityKey] as AcceptedData;
                            }
                    }
                }
            });
            return transformedActivity;
        });
    }

    private downloadCsv(data: Record<string, AcceptedData>[], filename: string): void {
        const csvConfig = mkConfig({
            filename,
            useKeysAsHeaders: true,
        });

        // Generating CSV string
        const csv = generateCsv(csvConfig)(data);
        download(csvConfig)(csv);
    }

    public export(activities: ActivityDetailVm[], filename: string): void {
        try {
            const csvData = this.getExportData(activities);
            this.downloadCsv(csvData, filename);
        } catch (error) {
            console.error('Error exporting CSV:', error);
        }
    }
}
